import React, { useCallback, useImperativeHandle, forwardRef, useEffect, useState, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { html } from '@codemirror/lang-html';
import { createTheme } from '@uiw/codemirror-themes';
import { tags as t } from '@lezer/highlight';
import { EditorView } from '@codemirror/view';
import { foldCode, codeFolding, foldEffect, foldService, unfoldEffect } from '@codemirror/language';
import { syntaxTree } from '@codemirror/language';
import { WidgetType } from '@codemirror/view';

// 自定义折叠占位符 Widget
class FoldPlaceholderWidget extends WidgetType {
    toDOM() {
        const span = document.createElement('span');
        span.className = 'cm-fold-placeholder-custom';
        span.textContent = '{...}';
        return span;
    }
}

// 自定义折叠服务，使用自定义占位符
const customFoldService = foldService.of((state, lineStart, lineEnd) => {
    return null; // 使用默认的折叠服务
});

const clickToFold = EditorView.domEventHandlers({
    mousedown: (event, view) => {
        // Get the clicked position
        const pos = view.posAtDOM(event.target);
        if (pos === null) return;

        // Check if the click is on a bracket/brace
        const line = view.state.doc.lineAt(pos);
        const char = line.text[pos - line.from];

        // Simple check for start brackets - robust check would involve syntax tree
        if (char === '{' || char === '[') {
            // Attempt to toggle fold at this position
            foldCode(view, { code: { from: pos, to: pos } });
            // We don't preventDefault to allow cursor placement, 
            // but if fold happens, visual change is enough.
        }
    }
});

const oneDarkTheme = createTheme({
    theme: 'dark',
    settings: {
        background: '#1e2227',
        foreground: '#abb2bf',
        caret: '#528bff',
        selection: '#3e4451',
        selectionMatch: '#3e4451',
        lineHighlight: 'rgba(255, 255, 255, 0.03)',
        gutterBackground: '#1e2227',
        gutterForeground: '#5c6370',
    },
    styles: [
        { tag: t.comment, color: '#5c6370', fontStyle: 'italic' },
        { tag: t.variableName, color: '#e06c75' },
        { tag: [t.string, t.special(t.brace)], color: '#98c379' },
        { tag: t.number, color: '#d19a66' },
        { tag: t.bool, color: '#c678dd' },
        { tag: t.null, color: '#56b6c2' },
        { tag: t.keyword, color: '#c678dd' },
        { tag: t.operator, color: '#56b6c2' },
        { tag: t.className, color: '#e5c07b' },
        { tag: t.definition(t.typeName), color: '#e5c07b' },
        { tag: t.typeName, color: '#e5c07b' },
        { tag: t.angleBracket, color: '#5c6370' },
        { tag: t.tagName, color: '#e06c75' },
        { tag: t.attributeName, color: '#d19a66' },
        { tag: t.propertyName, color: '#61afef' }, // JSON Keys
    ],
});

// 辅助函数：查找节点内的第一个括号位置
const findBracketPosition = (doc, from, to) => {
    for (let i = from; i < Math.min(from + 20, to); i++) {
        const char = doc.sliceString(i, i + 1);
        if (char === '{' || char === '[') {
            return i;
        }
    }
    return from;
};

// 辅助函数：计算折叠范围（排除空白字符）
const calculateFoldRange = (startLine, endLine) => {
    let foldFrom = startLine.to;
    let foldTo = endLine.from;
    
    // 找到第一行最后一个非空白字符的位置
    const startLineText = startLine.text;
    let lastNonWhitespace = startLineText.length;
    for (let i = startLineText.length - 1; i >= 0; i--) {
        if (startLineText[i] !== ' ' && startLineText[i] !== '\t') {
            lastNonWhitespace = i + 1;
            break;
        }
    }
    foldFrom = startLine.from + lastNonWhitespace;
    
    // 找到最后一行第一个非空白字符的位置
    const endLineText = endLine.text;
    let firstNonWhitespace = 0;
    for (let i = 0; i < endLineText.length; i++) {
        if (endLineText[i] !== ' ' && endLineText[i] !== '\t') {
            firstNonWhitespace = i;
            break;
        }
    }
    foldTo = endLine.from + firstNonWhitespace;
    
    return foldFrom < foldTo ? { from: foldFrom, to: foldTo } : null;
};

// 辅助函数：收集所有可折叠的节点
const collectFoldableNodes = (tree) => {
    const nodes = [];
    tree.iterate({
        enter: (node) => {
            if (!node.type) return;
            const nodeType = node.type.name;
            if (nodeType === 'Object' || nodeType === 'Array') {
                const from = node.from;
                const to = node.to;
                if (from >= to || (to - from) <= 2) return;
                nodes.push({ from, to, size: to - from });
            }
        }
    });
    return nodes;
};

const SmartEditor = forwardRef(({ value, onChange, language = 'json', readOnly = false, isMinified = false }, ref) => {
    const editorRef = React.useRef(null);
    const viewRef = React.useRef(null);

    // 监听编辑器实例变化
    useEffect(() => {
        if (editorRef.current?.view) {
            viewRef.current = editorRef.current.view;
        }
    }, [value]); // 当内容变化时更新 view

    useImperativeHandle(ref, () => ({
        foldAll: () => {
            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                console.warn('Editor view not available');
                return;
            }

            try {
                const tree = syntaxTree(view.state);
                const nodes = collectFoldableNodes(tree);
                
                if (nodes.length === 0) return;
                
                // 从后往前折叠，避免位置偏移（先折叠内层节点，再折叠外层节点）
                nodes.sort((a, b) => b.from - a.from);
                
                // 逐个应用折叠效果，确保每次使用最新状态
                nodes.forEach(({ from, to }) => {
                    try {
                        const startLine = view.state.doc.lineAt(from);
                        const endLine = view.state.doc.lineAt(to);
                        
                        // 只有跨越多行的节点才能折叠
                        if (endLine.number <= startLine.number) return;
                        
                        const bracketPos = findBracketPosition(view.state.doc, from, to);
                        const foldRange = foldCode(view, { code: { from: bracketPos, to: bracketPos } });
                        
                        if (foldRange?.from !== undefined && foldRange?.to !== undefined) {
                            view.dispatch({ effects: [foldEffect.of(foldRange)] });
                        } else {
                            // 使用备选方案：计算折叠范围
                            const calculatedRange = calculateFoldRange(startLine, endLine);
                            if (calculatedRange) {
                                view.dispatch({ effects: [foldEffect.of(calculatedRange)] });
                            }
                        }
                    } catch (e) {
                        // 静默处理错误，避免影响其他节点的折叠
                    }
                });
            } catch (error) {
                console.error('Error in foldAll:', error);
            }
        },
        unfoldAll: () => {
            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                console.warn('Editor view not available');
                return;
            }

            try {
                const tree = syntaxTree(view.state);
                const nodes = collectFoldableNodes(tree);
                
                if (nodes.length === 0) return;
                
                // 从内到外展开（从小到大），先展开子节点
                nodes.sort((a, b) => a.size - b.size);
                
                // 逐个应用展开效果，使用与 foldAll 相同的逻辑
                nodes.forEach(({ from, to }) => {
                    try {
                        const startLine = view.state.doc.lineAt(from);
                        const endLine = view.state.doc.lineAt(to);
                        
                        // 只有跨越多行的节点才能展开
                        if (endLine.number <= startLine.number) return;
                        
                        const bracketPos = findBracketPosition(view.state.doc, from, to);
                        const foldRange = foldCode(view, { code: { from: bracketPos, to: bracketPos } });
                        
                        if (foldRange?.from !== undefined && foldRange?.to !== undefined) {
                            view.dispatch({ effects: [unfoldEffect.of({ from: foldRange.from, to: foldRange.to })] });
                        } else {
                            // 使用备选方案：计算折叠范围
                            const calculatedRange = calculateFoldRange(startLine, endLine);
                            if (calculatedRange) {
                                view.dispatch({ effects: [unfoldEffect.of(calculatedRange)] });
                            }
                        }
                    } catch (e) {
                        // 静默处理错误，避免影响其他节点的展开
                    }
                });
            } catch (error) {
                console.error('Error in unfoldAll:', error);
            }
        }
    }));

    const extensions = useMemo(() => {
        // 始终启用自动换行，包括压缩后的内容
        const baseExtensions = [codeFolding(), EditorView.lineWrapping, clickToFold];
        switch (language) {
            case 'json': return [json(), ...baseExtensions];
            case 'xml': return [xml(), ...baseExtensions];
            case 'html': return [html(), ...baseExtensions];
            default: return baseExtensions;
        }
    }, [language]);

    return (
        <div style={{ 
            height: '100%', 
            borderRadius: '0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <CodeMirror
                ref={(editor) => {
                    editorRef.current = editor;
                    if (editor?.view) {
                        viewRef.current = editor.view;
                    }
                }}
                value={value}
                height="100%"
                theme={oneDarkTheme}
                extensions={extensions}
                onChange={onChange}
                readOnly={readOnly}
                basicSetup={{
                    foldGutter: true,
                    dropCursor: false,
                    allowMultipleSelections: false,
                    indentOnInput: false,
                    lineNumbers: true,
                    highlightActiveLineGutter: true,
                    highlightActiveLine: true,
                    bracketMatching: true,
                    closeBrackets: true,
                    autocompletion: true,
                    history: true,
                }}
                style={{ 
                    fontSize: '14px', 
                    fontFamily: '"JetBrains Mono", monospace', 
                    height: '100%',
                    padding: '0'
                }}
            />
        </div>
    );
});

SmartEditor.displayName = 'SmartEditor';

export default SmartEditor;
